import React, {ReactNode} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../PortalDocument";
import {Tree, TreeEventNodeEvent} from "primereact/tree";
import Observer from "../../../../shared/class/Observer";
import LocationEvent from "../../../viewer/LocationEvent";
import {
	Annotation,
	DocMdp,
	Field,
	Information,
	MetadataInformation,
	MetadataRectangle,
	MetadataRectangleInterface,
	MetadataSignature,
	Positions
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import TreeNode from "primereact/treenode";
import Translate from "../../../../shared/translation/Translate";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import MenuButton from "../../../../shared/menu/MenuButton";
import DocumentSettings from "../../DocumentSettings";
import {SignatureDialogDefinition} from "../../../dialog/components/signature/SignatureDialog";
import Parameter from "../../../dialog/parameter/Parameter";
import DialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import PortalDialogType from "../../../dialog/PortalDialogType";
import {PortalSignature} from "../../../../shared/models/PortalSignature";
import {PortalAddSignature} from "../../../../shared/models/PortalAddSignature";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";
import {FieldAnnotation} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources/metadata/FieldAnnotation";

interface SignatureListProperties extends I18nGlobals, DebugGlobals {
	visible: boolean;
	context: PortalContext;
	portalDocument?: Immutable<PortalDocumentInterface>;
	locationObserver?: Observer<LocationEvent>;
}

interface SignatureListStatus {
	signatures: Array<TreeNode>;
	selected: string | null;
	expanded: any;
}

enum EntryType {
	HEADER = "header",
	DATA = "data"
}

export default class SignatureList extends PortalPureComponent<SignatureListProperties, SignatureListStatus> {
	private readonly EMPTY_KEY = "empty";

	/**
	 * Signature list component constructor
	 */
	constructor(props: Readonly<SignatureListProperties>) {
		super(props);

		this.state = {
			signatures: [],
			selected: null,
			expanded: {}
		};

		this.onSelectionChange = this.onSelectionChange.bind(this);
		this.handleSelect = this.handleSelect.bind(this);
		this.expandList = this.expandList.bind(this);
		this.collapseList = this.collapseList.bind(this);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any
	): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		if (this.props.portalDocument!.get().id !== prevProps.portalDocument.get().id ||
			this.props.portalDocument!.get().updated !== prevProps.portalDocument.get().updated) {
			this.setState({
				selected: null
			});

			await this.buildSignatureList();
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		await this.buildSignatureList();
	}

	/**
	 * Build attachment data
	 */
	private async buildSignatureList(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let signatures: Array<MetadataSignature> = portalDocument.signatures;
		let result: Array<TreeNode> = [];
		let emptyFields: Array<any> = [];

		let nodeCount: number = 1;
		for (let signatureAnnotation of portalDocument.signatureAnnotations) {
			let hiddenSignature: boolean = FunctionHelper.applyValue(
				signatureAnnotation.hidden, Annotation.getHiddenDefault()
			);
			let icon: string = "icon icon-signature small";
			let label: string = Translate.value("signature.revision", {ns: "sidebar", revision: nodeCount});

			// handle empty fields
			if (!FunctionHelper.exists(signatureAnnotation.field?.signature)) {
				emptyFields.push(
					{
						key: signatureAnnotation.objectKey,
						label: FunctionHelper.applyValue(
							signatureAnnotation.field?.name, FieldAnnotation.getNameDefault()
							) +
							(hiddenSignature ? " (" + Translate.value(
								"signature.invisible", {ns: "sidebar"}
							) + ")" : ""),
						type: EntryType.DATA,
						icon: icon,
						data: signatureAnnotation
					} as any
				)

				nodeCount++;
				continue;
			}

			let docMDP: DocMdp | undefined = FunctionHelper.applyValue(
				portalDocument.metadata?.information?.docMDP, MetadataInformation.getDocMDPDefault()
			);
			if (docMDP !== DocMdp.None && signatures.length === nodeCount) {
				label = Translate.value("signature.signed", {ns: "sidebar"});
				icon = "icon icon-certificate";
			}

			let listEntries: Array<TreeNode> = [
				{
					label: Translate.value(
						"signature.date", {ns: "sidebar", date: signatureAnnotation.field?.signature?.date}
					),
					type: EntryType.DATA
				} as any,
				{
					label: Translate.value(
						"signature.contact", {ns: "sidebar", contact: signatureAnnotation.field?.signature?.contact}
					),
					type: EntryType.DATA
				} as any,
				{
					label: Translate.value(
						"signature.location", {ns: "sidebar", location: signatureAnnotation.field?.signature?.location}
					),
					type: EntryType.DATA
				} as any,
				{
					label: Translate.value(
						"signature.reason", {ns: "sidebar", reason: signatureAnnotation.field?.signature?.reason}
					),
					type: EntryType.DATA
				} as any,
				{
					label: Translate.value(
						"signature.field", {ns: "sidebar", field: signatureAnnotation.field?.name}
					) + (hiddenSignature ? " (" + Translate.value(
						"signature.invisible", {ns: "sidebar"}
					) + ")" : ""),
					type: EntryType.DATA
				} as any
			];

			result.push({
				key: signatureAnnotation.objectKey,
				label: label,
				type: EntryType.HEADER,
				data: signatureAnnotation,
				icon: icon,
				children: listEntries
			} as any);

			nodeCount++;
		}

		if (emptyFields.length > 0) {
			result.push({
				key: this.EMPTY_KEY,
				label: Translate.value("signature.unsigned", {ns: "sidebar"}),
				type: EntryType.HEADER,
				children: emptyFields
			} as any);
		}

		this.setState({
			signatures: result
		});
	}

	/**
	 * This is called if a tree node is getting selected
	 */
	private onSelectionChange(event: any): void {
		if (!FunctionHelper.exists(event.value)) {
			return;
		}

		if (event.value === this.EMPTY_KEY) {
			this.setState({expanded: {...this.state.expanded, [this.EMPTY_KEY]: true}});
			return;
		}

		this.setState({selected: event.value});
	}

	/**
	 * This is called if a tree node is selected
	 */
	private async handleSelect(event: TreeEventNodeEvent): Promise<void> {
		if (!FunctionHelper.exists(event.node.data)) {
			return;
		}

		let annotation: Annotation | undefined = event.node.data;
		let annotationPosition: Positions | undefined = FunctionHelper.applyValue(
			annotation?.positions, Annotation.getPositionsDefault()
		)[0];
		let rectangle: MetadataRectangle | undefined = (annotationPosition as any)?.rectangle;

		if (FunctionHelper.exists(this.props.locationObserver) &&
			!FunctionHelper.applyValue(annotation?.hidden, Annotation.getHiddenDefault())) {
			await this.props.locationObserver!.notify({
				target: MetadataRectangle.fromJson({
					width: UnitHelper.unitToPx(
						FunctionHelper.applyValue(rectangle?.width, MetadataRectangle.getWidthDefault()),
						LengthUnit.POINTS, this.props.portalResolution
					),
					height: UnitHelper.unitToPx(
						FunctionHelper.applyValue(rectangle?.height, MetadataRectangle.getHeightDefault()),
						LengthUnit.POINTS, this.props.portalResolution
					),
					x: UnitHelper.unitToPx(
						FunctionHelper.applyValue(rectangle?.x, MetadataRectangle.getXDefault()),
						LengthUnit.POINTS, this.props.portalResolution
					),
					y: UnitHelper.unitToPx(
						FunctionHelper.applyValue(rectangle?.y, MetadataRectangle.getYDefault()),
						LengthUnit.POINTS, this.props.portalResolution
					)
				} as MetadataRectangleInterface),
				pageNumber: FunctionHelper.applyValue(annotation?.page, Annotation.getPageDefault())
			});
		}
	}

	/**
	 * Expands all Tree entries
	 */
	private expandList(): void {
		let expanded: any = {};

		for (let signature of this.state.signatures) {
			expanded[signature.key!] = true;
		}

		this.setState({
			expanded: expanded
		});
	}

	/**
	 * Collapses all Tree entries
	 */
	private collapseList(): void {
		this.setState({
			expanded: {}
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalInfo: Information = this.props.context.info;
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let isPortfolioFile: boolean = portalDocument.isPortfolioFile();
		let field: Field | undefined = portalDocument.getSignatureField(this.state.selected);

		return (
			<div className="body" style={this.props.visible ? {} : {display: "none"}}
				 data-help="global.sidebar.signatures" data-position="right">
				<div className="toolbar">
					<MenuButton {...this.props} icon="icon icon-expand" onClick={this.expandList}
								tooltip={Translate.value("signature.expand", {ns: "sidebar"})}/>
					<MenuButton {...this.props} icon="icon icon-collapse" onClick={this.collapseList}
								tooltip={Translate.value("signature.collapse", {ns: "sidebar"})}/>
					<div className="separator"/>
					{(!this.props.documentChecks || (SignatureDialogDefinition.isEnabled(portalInfo) && !isPortfolioFile)) &&
						<MenuButton {...this.props} icon="icon icon-signature small"
									disabled={
										this.props.documentChecks &&
										(
											portalDocument.isLocked() ||
											!SignatureDialogDefinition.isActive(
												[portalDocument], portalInfo, this.props.signatureEditProfile
											) || this.state.selected === null || !FunctionHelper.exists(field) ||
											FunctionHelper.exists(field?.signature)
										)
									}
									tooltip={Translate.value("signature.add", {ns: "sidebar"})}
									onClick={async () => {
										let command: () => Promise<void> = async () => {
											let signatureParameter: Parameter<PortalSignature> =
												DialogDefault.getParameter(
													PortalDialogType.SIGNATURE, [portalDocument]
												);

											let addParameter: PortalAddSignature =
												signatureParameter.data.add as PortalAddSignature;
											addParameter.fieldName = field?.name;
											addParameter.appearance!.page = FunctionHelper.applyValue(
												field?.annotation![0].page, 1
											);
											addParameter.visible = true;
											signatureParameter.data.add = addParameter;

											this.props.context.getPage().setState({
												signatureParameter: signatureParameter,
												showSignatureDialog: true
											});
										}

										if (DocumentSettings.checkActiveMouseFunction(this.props)) {
											DocumentSettings.confirmResetMouseFunction(this.props, command);
											return;
										}

										await command();
									}}/>
					}
				</div>
				<div className="signature-list">
					{this.state.signatures.length > 0 &&
						<Tree value={this.state.signatures} selectionMode="single" selectionKeys={this.state.selected}
							  expandedKeys={this.state.expanded} propagateSelectionDown={false}
							  onSelectionChange={this.onSelectionChange} onSelect={this.handleSelect}
							  onToggle={e => {
								  this.setState({expanded: e.value})
							  }}/>
					}
					{this.state.signatures.length === 0 &&
						<p className="no-results">
							<Translate {...this.props} data="signature.empty" namespace="sidebar"/>
						</p>
					}
				</div>
				<div className="toolbar inverted"/>
			</div>
		);
	}
}